/**
 * Atlas Semantic Search — Slice 2 + Msg 544 polish.
 *
 * Progressive rendering:
 *  Stage 1 (`POST /atlas/search`) — Exa cards render immediately with
 *    photos + snippet + domain. Skeleton for reasoning + TravelBio.
 *  Stage 2 (`POST /atlas/search/enrich`, one call per column, fired
 *    in parallel after Stage 1 lands) — merges match_reasoning and
 *    TravelBio overlays into place.
 *
 * Detail modal (`POST /atlas/search/result-detail`) — clicking a card
 * anywhere in the body opens a full-page drawer with hero photo,
 * match %, LLM summary, quick facts, tips, promos, notices, reviews.
 *
 * "Save to client" button is MOCKED (toast-only stub for Slice 3).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search as SearchIcon,
  Loader2,
  X,
  Plus,
  ExternalLink,
  Bookmark,
  AlertTriangle,
  Info,
  Sparkles,
  Clock,
  Trash2,
  Globe,
  BookOpen,
  Wrench,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClientPickerModal } from "@/pages/atlas/AtlasClientsPages";
import ComparisonCard from "@/components/atlas/ComparisonCard";
import FactSourceInfoBadge from "@/pages/atlas/FactSourceInfoBadge";
import AtlasSmartCompareFiltered from "@/pages/atlas/AtlasSmartCompareFiltered";

const MAX_COLUMNS = 5;

// Msg 555 — Recent searches (localStorage). Keyed per-user so multiple
// advisors on the same browser don't cross-pollute lists. Only the
// query string + filter params are stored — never Exa result bodies
// (Exa compliance).
const RECENT_MAX = 10;
const _recentKey = (userId) => `atlas.recent_searches.v1.${userId || "anon"}`;
function loadRecentSearches(userId) {
  try {
    const raw = window.localStorage.getItem(_recentKey(userId));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((r) => r && typeof r.q === "string").slice(0, RECENT_MAX);
  } catch { return []; }
}
function saveRecentSearches(userId, list) {
  try {
    window.localStorage.setItem(_recentKey(userId), JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch { /* quota exceeded etc — ignore */ }
}
function pushRecentSearch(userId, entry) {
  const list = loadRecentSearches(userId);
  const norm = (s) => (s || "").trim().toLowerCase();
  const filtered = list.filter((r) => norm(r.q) !== norm(entry.q));
  const next = [{ ...entry, at: new Date().toISOString() }, ...filtered].slice(0, RECENT_MAX);
  saveRecentSearches(userId, next);
  return next;
}

export default function AtlasSearchPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id || user?.email || "anon";
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [submitting, setSubmitting] = useState(false);
  const [refining, setRefining] = useState(null);
  const [envelope, setEnvelope] = useState(null);
  const [enrichedByCol, setEnrichedByCol] = useState({});
  const [enrichingCols, setEnrichingCols] = useState({});
  const [colPages, setColPages] = useState({});
  const [addTopicOpen, setAddTopicOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(null); // {slug, result}
  const [savePicker, setSavePicker] = useState(null); // card snapshot
  const [savedByUrl, setSavedByUrl] = useState({}); // {url: [{client_id, save_id}]}
  const [recentSearches, setRecentSearches] = useState(() => loadRecentSearches(userId));
  // Batch 2 (2026-08-07) — Atlas Smart Compare Phase B. When the
  // smart-compare parser (`POST /api/atlas/smart-compare/parse`)
  // returns { mode: "filtered_list" } with confidence >= 0.5 AND at
  // least one entity, we render <AtlasSmartCompareFiltered/> instead
  // of dispatching to the existing Exa `/atlas/search`. General mode
  // and low-confidence + zero-entity plans delegate to the existing
  // flow unchanged.
  const [smartFilteredPlan, setSmartFilteredPlan] = useState(null);
  // D3 (2026-08-08) — Session client persistence. After the advisor
  // saves a card to a client via the ClientPickerModal, remember the
  // choice for subsequent Save clicks in this session. Not persisted
  // to Mongo — cleared on tab close or explicit clear.
  const [sessionClient, setSessionClient] = useState(null);   // {client_id, client_name}
  const inputRef = useRef(null);

  const refreshSavedLookup = useCallback(async (urls) => {
    if (!urls || urls.length === 0) return;
    try {
      const { data } = await api.post("/atlas/saves/lookup", { urls });
      setSavedByUrl((prev) => ({ ...prev, ...(data.saved_by_url || {}) }));
    } catch { /* ignore */ }
  }, []);

  const openSavePicker = useCallback((result) => {
    // Build a card snapshot from either a card or a detail result.
    setSavePicker({
      title: result.title || "",
      entity_name: result.entity_name || result.title || "",
      entity_slug: result.entity_slug || null,
      entity_slugs: result.entity_slugs || result.travelbio_resolved_slugs || [],
      url: result.url || "",
      image_url: result.image_url || null,
      snippet: result.snippet || "",
      source: result.source_kind || "single_entity",
      publication_name: result.publication_name || null,
      article_url: result.article_url || null,
      article_title: result.article_title || null,
      match_percentage: result.match_percentage || null,
      saved_from_query: query.trim(),
    });
  }, [query]);

  const enrichColumn = useCallback(async (colSlug, results, intent, q) => {
    if (!results || results.length === 0) return;
    setEnrichingCols((prev) => ({ ...prev, [colSlug]: true }));
    try {
      const { data } = await api.post("/atlas/search/enrich", {
        query: q,
        parsed_intent: intent,
        column_slug: colSlug,
        results,
      });
      setEnrichedByCol((prev) => ({
        ...prev,
        [colSlug]: { ...(prev[colSlug] || {}), ...(data.enriched_by_id || {}) },
      }));
      // Msg 545 · listicle expansion — the server may return a
      // FLATTENED list of cards longer than what Stage 1 returned. If
      // so, swap the column's `results` in place so listicle-derived
      // entity cards render.
      if (data.expanded_results && data.expanded_results.length > 0) {
        setEnvelope((prev) => {
          if (!prev || !prev.columns || !prev.columns[colSlug]) return prev;
          const nextCols = {
            ...prev.columns,
            [colSlug]: {
              ...prev.columns[colSlug],
              results: data.expanded_results,
              expanded: true,
            },
          };
          return { ...prev, columns: nextCols };
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[atlas.search.enrich] fail", colSlug, err);
    } finally {
      setEnrichingCols((prev) => ({ ...prev, [colSlug]: false }));
    }
  }, []);

  const runSearch = useCallback(async (q, opts = {}) => {
    if (!q || q.trim().length < 2) return;
    let trimmed = q.trim();
    // Msg 569 · Phase H — toggle state can be forced by the caller
    // (e.g. `Try Web search instead` empty-state button flips OFF
    // before the re-run). Otherwise read from the URL.
    const useDbOnly = opts.dbOnly !== undefined
      ? !!opts.dbOnly
      : (searchParams.get("db_only") === "1");
    setSubmitting(true);
    setEnrichedByCol({});
    setSmartFilteredPlan(null);
    setEnvelope({ __placeholder: true, column_order: ["_loading_a", "_loading_b"], columns: {} });
    // Msg 555 — sync the URL so browser back/forward + shareable links
    // restore the exact search. `replace: true` when auto-running from
    // an inbound URL avoids piling on history entries.
    if (!opts.fromUrl) {
      const params = { q: trimmed };
      if (useDbOnly) params.db_only = "1";
      setSearchParams(params, { replace: false });
    }

    // 2026-08-10 · DB-first sub_folder walker — hoisted so the second
    // `try` block (which posts to /atlas/search) can access them.
    let subFolderHint = null;
    let parsedEntities = null;

    // Batch 2 (2026-08-07) — Atlas Smart Compare intercept. Fire the
    // parser BEFORE the existing Exa `/atlas/search`. If it returns a
    // high-confidence filtered_list plan with resolvable entities, we
    // render the new Stay22-powered filtered column layout and skip
    // the Exa path entirely. Everything else (general mode, low
    // confidence, empty entities, or a parse failure) delegates to the
    // existing search unchanged. Skip the intercept in db_only mode —
    // that toggle explicitly asks for the TravelBio DB-only Exa flow.
    if (!useDbOnly) {
      try {
        const { data: plan } = await api.post("/atlas/smart-compare/parse",
          { query: trimmed });
        if (plan
            && plan.mode === "filtered_list"
            && Number(plan.confidence || 0) >= 0.5
            && Array.isArray(plan.entities)
            && plan.entities.length > 0) {
          // Persist recent search (query only — never Exa bodies).
          setRecentSearches(pushRecentSearch(userId, { q: trimmed }));
          setSmartFilteredPlan(plan);
          setEnvelope(null);
          setSubmitting(false);
          return;
        }
        // 2026-08-10 · DB-first sub_folder walker — pass the parsed
        // hint + normalised entities through to /atlas/search so the
        // backend can attempt a DB folder lookup FIRST (see
        // `atlas_knowledge.atlas_search`). We still rewrite the query
        // to include the hint as a fallback prefix — the Exa side is
        // only used for entities that miss the DB walker.
        if (plan?.sub_folder_hint
            && Array.isArray(plan.entities)
            && plan.entities.length >= 2) {
          const hint = String(plan.sub_folder_hint).trim();
          if (hint) {
            subFolderHint = hint;
            parsedEntities = plan.entities;
            trimmed = plan.entities.map((e) => `${hint} ${e}`).join(" vs ");
          }
        }
        // Fall through to the existing Exa search for general mode
        // and low-confidence outcomes. Don't surface the plan to the
        // Exa flow — the existing parser handles that path on its own.
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[atlas.smart-compare.parse] fail — falling back to Exa", err);
      }
    }

    try {
      const searchBody = { query: trimmed, db_only: useDbOnly };
      if (subFolderHint && parsedEntities) {
        searchBody.sub_folder_hint = subFolderHint;
        searchBody.parsed_entities = parsedEntities;
      }
      const { data } = await api.post("/atlas/search", searchBody);
      setEnvelope(data);
      setColPages({});
      // Msg 555 — persist the query (NOT the Exa result bodies) to
      // localStorage under the current advisor's id.
      setRecentSearches(pushRecentSearch(userId, { q: trimmed }));
      // Fire enrichment per column in parallel — SKIPPED in compare
      // mode because sides[i].cards are already enriched inline by the
      // backend and a second enrich pass would clobber comparison_facts.
      // Also SKIPPED in db_only mode — those cards are already fully
      // hydrated from the internal DB and calling Exa enrich would
      // fetch external data we intentionally suppressed.
      const intent = data.parsed_intent;
      const allUrls = [];
      const skipEnrich = !!data.compare_mode || !!data.db_only;
      (data.column_order || []).forEach((slug) => {
        const results = data.columns?.[slug]?.results || [];
        results.forEach((r) => { if (r.url) allUrls.push(r.url); });
        if (!skipEnrich) enrichColumn(slug, results, intent, trimmed);
      });
      // Msg 550 — batch-check which URLs are already saved.
      refreshSavedLookup(allUrls);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[atlas.search] fail", err);
      toast.error(err?.response?.data?.detail || "Search failed. Try again.");
      setEnvelope(null);
    } finally { setSubmitting(false); }
  }, [enrichColumn, refreshSavedLookup, setSearchParams, userId, searchParams]);

  // Msg 555 — URL → state hydration. On mount and on any browser
  // back/forward that changes `?q=`, re-run the search so the results
  // match the URL. Guarded by `urlRunFor` ref so we don't re-fire when
  // the user's own submission already advanced the URL.
  const urlRunFor = useRef(null);
  useEffect(() => {
    const qParam = (searchParams.get("q") || "").trim();
    if (!qParam) {
      // URL has no query → landing state. Clear envelope if we had one.
      if (envelope && !envelope.__placeholder) {
        setEnvelope(null);
        setEnrichedByCol({});
        setColPages({});
      }
      if (smartFilteredPlan) setSmartFilteredPlan(null);
      urlRunFor.current = "";
      // Sync input field with URL when user navigates back to landing.
      if (query && !submitting) setQuery("");
      return;
    }
    if (urlRunFor.current === qParam) return;
    urlRunFor.current = qParam;
    setQuery(qParam);
    runSearch(qParam, { fromUrl: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Msg 555 — clear the recent-searches list for the current advisor.
  const clearRecentSearches = useCallback(() => {
    saveRecentSearches(userId, []);
    setRecentSearches([]);
    toast.success("Cleared recent searches.");
  }, [userId]);

  const runRefine = useCallback(async (opts) => {
    if (!envelope) return;
    setRefining(opts.pagingSlug || "refine");
    try {
      const body = {
        original_query: query.trim(),
        parsed_intent: envelope.parsed_intent,
        active_columns: opts.active_columns ?? envelope.column_order,
        add_destinations: opts.add_destinations || [],
        remove_destinations: opts.remove_destinations || [],
        page: opts.page || undefined,
      };
      const { data } = await api.post("/atlas/search/refine", body);
      setEnvelope(data);
      if (opts.page) setColPages((prev) => ({ ...prev, ...opts.page }));
      // Enrich any NEW columns (compare column_order to enrichedByCol keys).
      const intent = data.parsed_intent;
      (data.column_order || []).forEach((slug) => {
        if (!enrichedByCol[slug]) {
          const rs = data.columns?.[slug]?.results || [];
          enrichColumn(slug, rs, intent, query.trim());
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[atlas.search.refine] fail", err);
      toast.error(err?.response?.data?.detail || "Refine failed.");
    } finally { setRefining(null); }
  }, [envelope, query, enrichedByCol, enrichColumn]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    runSearch(query);
  };

  const removeColumn = (slug) => {
    const remaining = (envelope.column_order || []).filter((s) => s !== slug);
    runRefine({ active_columns: remaining, remove_destinations: [slug] });
    setEnrichedByCol((prev) => { const next = { ...prev }; delete next[slug]; return next; });
  };

  const addColumn = async (slug) => {
    const clean = (slug || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (!clean) return;
    if ((envelope.column_order || []).includes(clean)) {
      toast.info("That destination is already open.");
      return;
    }
    if ((envelope.column_order || []).length >= MAX_COLUMNS) {
      toast.error(`Maximum ${MAX_COLUMNS} columns.`); return;
    }
    setAddTopicOpen(false);
    runRefine({
      active_columns: [...(envelope.column_order || []), clean],
      add_destinations: [clean],
    });
  };

  const viewMore = (slug) => {
    const nextPage = (colPages[slug] || 0) + 1;
    runRefine({
      active_columns: envelope.column_order,
      page: { [slug]: nextPage },
      pagingSlug: slug,
    });
  };

  const intent = envelope?.parsed_intent || null;
  const isLoadingPlaceholder = envelope?.__placeholder;
  const dbOnly = searchParams.get("db_only") === "1";

  const setDbOnly = (val) => {
    // Msg 569 · Phase H — flip the URL flag and re-run the current
    // query. If there's nothing in the input we just update the URL so
    // the toggle sticks for the next submit.
    const params = new URLSearchParams(searchParams);
    if (val) params.set("db_only", "1"); else params.delete("db_only");
    setSearchParams(params, { replace: true });
    const q = (searchParams.get("q") || query || "").trim();
    if (q.length >= 2) runSearch(q, { dbOnly: val, fromUrl: false });
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="p-6 md:p-10 max-w-[1600px] mx-auto">
        <div className="max-w-3xl">
          <h1
            className="text-tb-navy text-3xl md:text-4xl font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
            data-testid="atlas-search-title"
          >
            Atlas Search
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm">
            Ask anything in plain English. We'll compare destinations side-by-side and overlay your TravelBio knowledge on every result.
          </p>
        </div>

        <form onSubmit={handleSearchSubmit} className="mt-6 max-w-4xl">
          {/* Msg 569 · Phase H — source-toggle segmented control */}
          <div className="mb-3 inline-flex items-center rounded-full border border-tb-navy/20 bg-white p-1"
               data-testid="atlas-search-source-toggle">
            <button type="button"
              onClick={() => setDbOnly(false)}
              data-testid="atlas-search-toggle-web"
              aria-pressed={!dbOnly}
              className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full font-hand text-xs transition ${
                !dbOnly ? "bg-tb-navy text-tb-gold-bright shadow-sm"
                        : "text-tb-navy/70 hover:text-tb-navy"
              }`}>
              <Globe className="w-3.5 h-3.5" /> Web (Exa + TravelBio)
            </button>
            <button type="button"
              onClick={() => setDbOnly(true)}
              data-testid="atlas-search-toggle-db"
              aria-pressed={dbOnly}
              className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-full font-hand text-xs transition ${
                dbOnly ? "bg-tb-navy text-tb-gold-bright shadow-sm"
                       : "text-tb-navy/70 hover:text-tb-navy"
              }`}>
              <BookOpen className="w-3.5 h-3.5" /> TravelBio DB Only
            </button>
          </div>
          <div className="flex items-center gap-2 h-14 px-5 rounded-full border border-tb-navy/25 bg-white shadow-sm focus-within:border-tb-navy focus-within:shadow"
               data-testid="atlas-search-bar">
            <SearchIcon className="w-5 h-5 text-tb-navy/50 flex-shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Compare Aruba vs Jamaica all-inclusive for family of five... or search anything"
              className="flex-1 h-full outline-none font-hand text-tb-navy placeholder-tb-navy/35"
              data-testid="atlas-search-input"
            />
            <button
              type="submit"
              disabled={submitting || query.trim().length < 2}
              data-testid="atlas-search-submit"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
              Search
            </button>
          </div>
          <p className="mt-2 font-hand text-tb-navy/40 text-xs">
            Tips: "compare X vs Y for family of five" · "best luxury cruises for October" · "boutique hotels in bali with pool"
          </p>
        </form>

        {intent && !isLoadingPlaceholder && (intent.parser_status === "ok" || intent.parser_status === "ok_cached") && (
          <div className="mt-5 flex flex-wrap items-center gap-1.5" data-testid="atlas-search-intent-chips">
            <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">Detected</span>
            <IntentPill label={intent.intent} testId="atlas-search-intent-mode" />
            {(intent.destinations || []).map((d) => (
              <IntentPill key={`d-${d}`} label={`📍 ${d.replace(/-/g, " ")}`}
                          testId={`atlas-search-intent-dest-${d}`} />
            ))}
            {intent.category && (
              <IntentPill label={`🏷️ ${intent.category.replace(/-/g, " ")}`} testId="atlas-search-intent-category" />
            )}
            {intent.traveler_profile?.family_size && (
              <IntentPill label={`👨‍👩‍👧 family of ${intent.traveler_profile.family_size}`} testId="atlas-search-intent-family" />
            )}
            {intent.traveler_profile?.traveler_type && !intent.traveler_profile.family_size && (
              <IntentPill label={`👥 ${intent.traveler_profile.traveler_type}`} testId="atlas-search-intent-travelertype" />
            )}
            {intent.traveler_profile?.budget_band && (
              <IntentPill label={`💰 ${intent.traveler_profile.budget_band}`} testId="atlas-search-intent-budget" />
            )}
            {(intent.keywords_freeform || []).slice(0, 4).map((k) => (
              <IntentPill key={`k-${k}`} label={`✨ ${k}`} testId={`atlas-search-intent-kw-${k.replace(/\s+/g, "-")}`} />
            ))}
            {intent.parser_status === "ok_cached" && (
              <span className="text-[9px] uppercase tracking-widest font-hand text-tb-navy/40 ml-1">· parse cached</span>
            )}
          </div>
        )}

        {envelope && !isLoadingPlaceholder && envelope.db_only && (
          <div className="mt-4 flex items-center gap-2" data-testid="atlas-search-db-only-badge">
            <span className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-tb-gold-bright/50 bg-tb-gold-bright/15 text-tb-navy font-hand text-[11px]">
              <BookOpen className="w-3 h-3" />
              Showing TravelBio DB only
            </span>
          </div>
        )}

        {envelope && envelope.db_only && envelope.empty_state_hint
              && (envelope.results || []).length === 0 && !isLoadingPlaceholder && (
          <div className="mt-6 max-w-2xl rounded-2xl border-2 border-dashed border-tb-navy/25 bg-tb-navy/[0.02] p-6"
               data-testid="atlas-search-db-only-empty">
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-full bg-tb-gold-bright/20 text-tb-navy inline-flex items-center justify-center shrink-0">
                <Wrench className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <h3 className="text-tb-navy text-lg font-semibold"
                    style={{ fontFamily: '"Playfair Display", serif' }}>
                  No TravelBio data on this yet
                </h3>
                <p className="mt-1 font-hand text-[12px] text-tb-navy/70">
                  {envelope.empty_state_hint}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button type="button"
                    onClick={() => setDbOnly(false)}
                    data-testid="atlas-search-empty-try-web"
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs">
                    <Globe className="w-3.5 h-3.5" /> Try Web search instead
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isLoadingPlaceholder ? (
          <ColumnsSkeleton count={2} />
        ) : smartFilteredPlan ? (
          <AtlasSmartCompareFiltered
            plan={smartFilteredPlan}
            query={query}
            onOpenSavePicker={openSavePicker}
            sessionClient={sessionClient}
            onClearSessionClient={() => setSessionClient(null)}
          />
        ) : envelope && envelope.compare_mode ? (
          <CompareResults
            envelope={envelope}
            activeCol={parseInt(searchParams.get("col") || "0", 10)}
            onChangeActiveCol={(idx) => {
              const nextParams = new URLSearchParams(searchParams);
              nextParams.set("col", String(idx));
              setSearchParams(nextParams, { replace: true });
            }}
            onOpenSavePicker={openSavePicker}
            onOpenDetail={(card) => setDetailOpen({
              slug: card.result_id?.split(":")[0] || envelope.column_order?.[0],
              result: card,
            })}
            savedByUrl={savedByUrl}
            onSavedAttribute={() => {
              // Msg 556 · C.3 — re-run the current search so the newly
              // curated attribute flows through the overlay override
              // and the 🔧 TravelBio chip appears on the bullet.
              const q = searchParams.get("q") || query;
              if (q) runSearch(q, { fromUrl: false });
            }}
          />
        ) : envelope ? (
          <div className="mt-6 flex gap-4 overflow-x-auto pb-4" data-testid="atlas-search-columns">
            {(envelope.column_order || []).map((slug) => (
              <ColumnPanel
                key={slug}
                slug={slug}
                column={envelope.columns[slug]}
                enrichedById={enrichedByCol[slug] || {}}
                enriching={!!enrichingCols[slug]}
                onRemove={() => removeColumn(slug)}
                onViewMore={() => viewMore(slug)}
                paging={refining === slug}
                onCardClick={(r) => setDetailOpen({ slug, result: r })}
                savedByUrl={savedByUrl}
                onOpenSavePicker={openSavePicker}
              />
            ))}
            {(envelope.column_order || []).length < MAX_COLUMNS && (
              <AddColumnPanel
                open={addTopicOpen}
                onOpenChange={setAddTopicOpen}
                onAdd={addColumn}
                busy={refining === "refine"}
              />
            )}
          </div>
        ) : (
          <div className="mt-10 max-w-3xl">
            {/* Msg 555 — Recent searches. Only rendered on the landing
                view (no envelope). Clicking a chip re-runs the query;
                Clear wipes the localStorage list for this advisor. */}
            {recentSearches.length > 0 && (
              <div className="mb-6" data-testid="atlas-recent-searches">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/60 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" />
                    Recent searches
                  </p>
                  <button
                    type="button"
                    onClick={clearRecentSearches}
                    data-testid="atlas-recent-searches-clear"
                    className="inline-flex items-center gap-1 h-7 px-2 rounded-full text-tb-navy/60 hover:text-tb-navy hover:bg-tb-navy/5 font-hand text-[11px]"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentSearches.map((r, i) => (
                    <button
                      key={`${r.q}-${i}`}
                      type="button"
                      onClick={() => { setQuery(r.q); setSearchParams({ q: r.q }); }}
                      data-testid={`atlas-recent-search-chip-${i}`}
                      title={r.at ? new Date(r.at).toLocaleString() : ""}
                      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-tb-navy/20 bg-white hover:bg-tb-navy hover:text-tb-gold-bright text-tb-navy font-hand text-xs max-w-[280px] transition-colors"
                    >
                      <SearchIcon className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{r.q}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6">
              <p className="font-hand text-tb-navy text-sm">
                Start with something like <span className="">"compare aruba vs jamaica all inclusive for family of five"</span> or <span className="">"boutique hotels near Kyoto with cherry blossom views"</span>.
              </p>
              <p className="mt-2 font-hand text-tb-navy/60 text-xs">
                Every result blends fresh web content with the TravelBio brain — attributes, advisor tips, active promos, and traveler reviews already contributed by advisors.
              </p>
            </div>
          </div>
        )}
        {/* Msg 580 · J.5 — subtle latency footer. Rendered once per
            search result. Non-intrusive gray text at the bottom of
            the results area so we (and the advisor) can see when a
            search feels slow. */}
        {envelope && !isLoadingPlaceholder && typeof envelope.total_ms === "number" && (
          <div
            data-testid="atlas-search-latency-footer"
            className="mt-6 mb-2 flex items-center justify-end gap-1 font-mono text-[10px] text-tb-navy/40 select-none"
            title={envelope.timing_ms
              ? `parse+exa ${envelope.timing_ms.exa_search}ms · enrich ${envelope.timing_ms.enrich}ms · compare_facts ${envelope.timing_ms.compare_facts}ms`
              : undefined}
          >
            Loaded in {(envelope.total_ms / 1000).toFixed(1)}s
          </div>
        )}
      </div>

      {detailOpen && (
        <DetailDrawer
          query={query}
          slug={detailOpen.slug}
          result={detailOpen.result}
          intent={envelope?.parsed_intent}
          preloadedEnrichment={(enrichedByCol[detailOpen.slug] || {})[detailOpen.result.result_id]}
          savedByUrl={savedByUrl}
          onOpenSavePicker={openSavePicker}
          onClose={() => setDetailOpen(null)}
        />
      )}
      {savePicker && (
        <ClientPickerModal
          cardSnapshot={savePicker}
          preselectedClientId={savePicker._preselect_client_id || sessionClient?.client_id || null}
          onClose={() => setSavePicker(null)}
          onSaved={(clientId, saveId, clientName) => {
            setSavedByUrl((prev) => {
              const arr = prev[savePicker.url] || [];
              const already = arr.find((x) => x.client_id === clientId);
              if (already) return prev;
              return { ...prev, [savePicker.url]: [...arr, { client_id: clientId, save_id: saveId }] };
            });
            // D3 — remember for this session so the next Save on any
            // smart-compare card can auto-select the same client.
            setSessionClient({
              client_id: clientId,
              client_name: clientName || sessionClient?.client_name || "client",
            });
          }}
        />
      )}
    </div>
  );
}


/**
 * Msg 556 · Slice B — Compare Results renderer.
 *
 * Desktop (>= md): two-column grid, side_a=left, side_b=right, order
 * preserved from `envelope.sides` (never reordered by score/alphabet).
 *
 * Mobile (< md): sticky tab selector at top; only the active side's
 * cards render. Active tab is URL-persisted via `?col=0` / `?col=1`.
 *
 * Empty state per side ("No results for X · Try adjusting the query").
 */
function CompareResults({
  envelope,
  activeCol,
  onChangeActiveCol,
  onOpenSavePicker,
  onOpenDetail,
  savedByUrl,
  onSavedAttribute,
}) {
  const sides = envelope?.sides || [];
  if (sides.length === 0) return null;
  const safeCol = Math.max(0, Math.min(sides.length - 1, activeCol || 0));

  return (
    <div className="mt-6" data-testid="atlas-compare-results">
      {/* Modifier chips row — shown when match scoring is active. */}
      {envelope.match_scoring_active && (envelope.modifiers || []).length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5"
             data-testid="atlas-compare-modifiers">
          <span className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">
            Match scoring
          </span>
          {envelope.modifiers.map((m) => (
            <span
              key={m}
              className="inline-flex items-center h-6 px-2.5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-[10px] uppercase tracking-wider"
              data-testid={`atlas-compare-modifier-${m}`}
            >
              {m}
            </span>
          ))}
        </div>
      )}

      {/* MOBILE — sticky column-selector tabs. `md:hidden` gates it to
          screens < 768px so desktop keeps the two-column layout. */}
      <div className="md:hidden sticky top-0 z-20 -mx-6 px-6 bg-white border-b border-tb-navy/10 py-2"
           data-testid="atlas-compare-tabs">
        <div className="flex gap-1.5">
          {sides.map((s, i) => {
            const active = i === safeCol;
            return (
              <button
                key={s.slug || i}
                type="button"
                onClick={() => onChangeActiveCol(i)}
                data-testid={`atlas-compare-tab-${i}`}
                className={`flex-1 min-w-0 h-10 px-3 rounded-full font-hand text-xs truncate transition-colors ${
                  active
                    ? "bg-tb-navy text-tb-gold-bright shadow-sm"
                    : "bg-tb-navy/5 text-tb-navy/60 hover:bg-tb-navy/10"
                }`}
                aria-pressed={active}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* DESKTOP — two-column grid.  MOBILE — only active side. */}
      <div className="mt-2 md:mt-0 grid md:grid-cols-2 gap-4 md:gap-6">
        {sides.map((s, i) => (
          <SideColumn
            key={s.slug || i}
            side={s}
            sideIndex={i}
            hiddenOnMobile={i !== safeCol}
            onOpenSavePicker={onOpenSavePicker}
            onOpenDetail={onOpenDetail}
            savedByUrl={savedByUrl}
            onSavedAttribute={onSavedAttribute}
          />
        ))}
      </div>
    </div>
  );
}

// (2026-08-05) Bug fix — section titles used to render the raw
// user-typed side token (e.g. "anthem" or "adventure of the seas").
// Now prefers the resolved topic's `topic_name` and, when a
// depth-hint like "cabins" is present, formats as
// `{Depth Hint} on {Topic Name}`.
function _titleCase(s) {
  return (s || "").replace(/\b([a-z])/g, (m) => m.toUpperCase());
}
function _sectionTitleFor(side) {
  const card = side?.cards?.[0];
  if (card?.topic_name) {
    if (side.depth_hint) {
      const dh = _titleCase(side.depth_hint);
      // (2026-08-05) If the depth-hint resolved into a sub-folder
      // whose name equals the hint itself (e.g. "cabins on anthem"
      // → Anthem's "Cabins" sub-folder), the topic_name is already
      // the depth-hint. Format as "Cabins on Anthem of the Seas"
      // using the parent instead of "Cabins on Cabins".
      const topicNameLc = card.topic_name.trim().toLowerCase();
      if (topicNameLc === side.depth_hint.trim().toLowerCase()) {
        const parent = (card.breadcrumb || []).slice(-1)[0];
        if (parent?.name) return `${dh} on ${parent.name}`;
      }
      return `${dh} on ${card.topic_name}`;
    }
    return card.topic_name;
  }
  return side?.label || "";
}

function SideColumn({ side, sideIndex, hiddenOnMobile, onOpenSavePicker, onOpenDetail, savedByUrl, onSavedAttribute }) {
  const cards = side?.cards || [];
  return (
    <div
      className={hiddenOnMobile ? "hidden md:block" : "block"}
      data-testid={`atlas-compare-side-${sideIndex}`}
    >
      {/* Desktop-only side header (mobile uses the tab bar). */}
      <div className="hidden md:block mb-3">
        <h2
          className="text-tb-navy font-semibold"
          style={{ fontFamily: '"Playfair Display", serif', fontSize: "20px" }}
          data-testid={`atlas-compare-side-title-${sideIndex}`}
        >
          {_sectionTitleFor(side)}
        </h2>
        <p className="mt-0.5 font-hand text-[11px] text-tb-navy/50 uppercase tracking-wider">
          {side.entity_kind} · {(side.entity_type || "").replace(/_/g, " ")}
          {side.location ? ` · ${side.location}` : ""}
          {" · "}{cards.length} {cards.length === 1 ? "result" : "results"}
        </p>
      </div>

      {/* 2026-08-10 · DB-first sub_folder walker — Exa-fallback chip.
          When the backend couldn't find a matching sub-folder for this
          entity in the DB and fell back to Exa, `db_lookup_note` carries
          the reason. Render a subtle amber pill so the advisor knows
          this column is web-sourced, not TravelBio DB. */}
      {side?.db_lookup_note && (
        <div
          data-testid={`atlas-compare-db-lookup-note-${sideIndex}`}
          className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-300/60 bg-amber-50 px-3 py-1 font-hand text-[11px] text-amber-900"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
          Showing web results — {side.db_lookup_note}
        </div>
      )}

      {cards.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-6"
          data-testid={`atlas-compare-empty-${sideIndex}`}
        >
          <p className="font-hand text-tb-navy text-sm">
            No results found for <span className="">{side.label}</span>.
          </p>
        </div>
      ) : side?.ambiguous_candidates?.length ? (
        <AmbiguityCard side={side} sideIndex={sideIndex} />
      ) : side?.is_placeholder ? (
        <PlaceholderCard side={side} sideIndex={sideIndex} />
      ) : cards[0]?.topic_slug ? (
        // (2026-08-04) New enriched compare card — used when strict
        // resolver returned a single topic with the full projection.
        <EnrichedCompareCard card={cards[0]} sideIndex={sideIndex} />
      ) : (
        <div className="space-y-4">
          {cards.map((c) => (
            <ComparisonCard
              key={c.result_id || c.entity_name || c.url}
              card={c}
              onOpenSavePicker={onOpenSavePicker}
              onOpenDetail={onOpenDetail}
              savedByUrl={savedByUrl}
              onSavedAttribute={onSavedAttribute}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// (2026-08-04) Enriched compare card — mirrors the topic focus page:
// breadcrumb, cover placeholder, structured attributes, narrative
// facts, advisor tips, and clickable sub-folder chips.
function EnrichedCompareCard({ card, sideIndex }) {
  const [subFoldersOpen, setSubFoldersOpen] = React.useState(false);
  const [coverIdx, setCoverIdx] = React.useState(0);
  const subFolders = card.sub_folders || [];
  // (2026-08-05) When the topic has no per-folder `custom_attribute_schema`
  // (very common — most schemas live in the global attribute library),
  // synthesize schema rows from the attribute-typed facts themselves.
  // Each fact carries its own `attribute_key` + `attribute_label`, so we
  // can render the same rows the user saw on the topic focus page.
  const attrValues = card.attribute_values || {};
  const schemaFromDoc = card.custom_attribute_schema || [];
  const attrSchema = schemaFromDoc.length > 0
    ? schemaFromDoc
    : Object.entries(attrValues).map(([key, rec]) => ({
        key,
        label: rec?.label || key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()),
        type: rec?.type || "string",
      }));
  // Item 4 (2026-08-08) — Sort attributes case-insensitive by label so
  // matching names align on the same row across side-by-side compare
  // columns. Legacy compare + smart-compare general mode share this
  // card; smart-compare filtered-list uses a different card + shape and
  // is intentionally left alone.
  const attrSchemaSorted = [...attrSchema].sort((a, b) => {
    const la = String(a.label || a.key || "").toLowerCase();
    const lb = String(b.label || b.key || "").toLowerCase();
    return la.localeCompare(lb);
  });
  const advisorTips = card.advisor_tip_facts || [];
  const narrative = card.narrative_only_facts || [];
  // (2026-08-05) Photos + PDFs from backend `_hydrate_db_only_card`.
  const photos = card.photos || [];
  const pdfs = card.pdfs || [];
  // Resolve attachment URLs — backend returns paths like
  // `/api/uploads/file/…`; prepend the configured backend origin so
  // the <img> src resolves against the same host as the API.
  const backendOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const resolveUrl = (u) => {
    if (!u) return "";
    if (/^https?:\/\//i.test(u)) return u;
    return `${backendOrigin}${u.startsWith("/") ? "" : "/"}${u}`;
  };
  const activeCoverUrl = photos[coverIdx]?.url ? resolveUrl(photos[coverIdx].url) : null;
  const step = (delta) => setCoverIdx((i) => (i + delta + photos.length) % photos.length);
  return (
    <article
      className="rounded-2xl border border-tb-navy/15 bg-white overflow-hidden shadow-sm"
      data-testid={`atlas-compare-enriched-card-${sideIndex}`}
    >
      {/* Cover area — real photo when available, else navy placeholder. */}
      {activeCoverUrl ? (
        <div className="relative w-full aspect-[16/9] bg-tb-navy/5 group"
             data-testid={`atlas-compare-cover-photo-${sideIndex}`}>
          <img src={activeCoverUrl}
            alt={photos[coverIdx]?.name || card.topic_name || "cover"}
            className="w-full h-full object-cover"
            data-testid={`atlas-compare-cover-img-${sideIndex}`} />
          {photos.length > 1 && (
            <>
              <button type="button" onClick={() => step(-1)}
                aria-label="Previous photo"
                data-testid={`atlas-compare-cover-prev-${sideIndex}`}
                className="opacity-0 group-hover:opacity-100 transition absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/90 shadow border border-tb-navy/15 text-tb-navy inline-flex items-center justify-center text-lg">
                ‹
              </button>
              <button type="button" onClick={() => step(1)}
                aria-label="Next photo"
                data-testid={`atlas-compare-cover-next-${sideIndex}`}
                className="opacity-0 group-hover:opacity-100 transition absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/90 shadow border border-tb-navy/15 text-tb-navy inline-flex items-center justify-center text-lg">
                ›
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1"
                   data-testid={`atlas-compare-cover-dots-${sideIndex}`}>
                {photos.map((_, i) => (
                  <button key={i} type="button" onClick={() => setCoverIdx(i)}
                    aria-label={`Photo ${i + 1}`}
                    className={
                      "h-1.5 rounded-full transition-all " +
                      (i === coverIdx
                        ? "w-4 bg-white"
                        : "w-1.5 bg-white/60 hover:bg-white/90")
                    } />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="w-full aspect-[16/9] bg-gradient-to-br from-tb-navy/85 to-tb-navy/95 flex items-center justify-center relative"
             data-testid={`atlas-compare-cover-placeholder-${sideIndex}`}>
          <div className="text-tb-gold-bright/90 text-center px-6">
            <div className="text-[10px] uppercase tracking-widest font-hand opacity-70">
              {(card.category || card.entity_type || "topic").replace(/_/g, " ")}
            </div>
            <div className="mt-1 text-2xl font-semibold"
                 style={{ fontFamily: '"Playfair Display", serif' }}>
              {card.topic_name || card.title}
            </div>
          </div>
        </div>
      )}
      <div className="p-4">
        {/* Breadcrumb path */}
        {(card.breadcrumb || []).length > 0 && (
          <nav className="text-[10px] font-hand text-tb-navy/50 mb-2"
               data-testid={`atlas-compare-breadcrumb-${sideIndex}`}>
            {(card.breadcrumb || []).map((b, i) => (
              <React.Fragment key={b.slug}>
                {i > 0 && <span className="mx-1">›</span>}
                <Link to={`/atlas/topics/${b.slug}`}
                      className="hover:text-tb-navy">{b.name || b.slug}</Link>
              </React.Fragment>
            ))}
          </nav>
        )}
        {/* Title + open link */}
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <h3 className="text-tb-navy text-xl font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            {card.topic_name || card.title}
          </h3>
          <Link to={`/atlas/topics/${card.topic_slug}`}
            data-testid={`atlas-compare-open-${sideIndex}`}
            className="text-[11px] font-hand text-tb-navy/70 hover:text-tb-navy underline underline-offset-2">
            Open folder →
          </Link>
        </div>
        {card.topic_description && (
          <p className="mt-1 font-hand text-tb-navy/70 text-sm">
            {card.topic_description}
          </p>
        )}
        {/* Structured attributes */}
        {attrSchemaSorted.length > 0 && (
          <section className="mt-5" data-testid={`atlas-compare-attrs-${sideIndex}`}>
            <div className="text-sm font-semibold text-tb-navy/70 mb-2">
              Attributes
            </div>
            <div className="rounded-lg border border-tb-navy/10 divide-y divide-tb-navy/10">
              {attrSchemaSorted.slice(0, 12).map((row) => {
                // (2026-08-05) Prefer joined attribute_values from atlas_facts.
                // Falls back to legacy comparison_facts[row.key] for older rows.
                const rec = (card.attribute_values || {})[row.key];
                const val = rec?.value !== undefined && rec?.value !== null && rec?.value !== ""
                  ? rec.value
                  : (card.comparison_facts || {})[row.key];
                const hasVal = val !== null && val !== undefined && val !== "";
                return (
                  <div key={row.key} className="flex items-baseline gap-3 px-3 py-2">
                    <span
                      data-testid={`atlas-compare-attr-label-${sideIndex}-${row.key}`}
                      className="text-sm font-medium text-tb-navy/70 flex-shrink-0"
                    >
                      {row.label || row.key}
                    </span>
                    <span
                      data-testid={`atlas-compare-attr-value-${sideIndex}-${row.key}`}
                      className={
                        "ml-auto text-right " +
                        (hasVal
                          ? "text-base font-semibold text-tb-navy"
                          : "text-sm text-tb-navy/30 italic")
                      }
                    >
                      {hasVal ? String(val) : "Not filled in yet"}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {/* Advisor tips */}
        {advisorTips.length > 0 && (
          <section className="mt-5" data-testid={`atlas-compare-tips-${sideIndex}`}>
            <div className="text-sm font-semibold text-tb-navy/70 mb-2">
              Advisor tips ({advisorTips.length})
            </div>
            <ul className="space-y-2">
              {advisorTips.slice(0, 3).map((t, i) => (
                <li key={i} className="rounded-lg border border-tb-gold-bright/50 bg-tb-gold-bright/10 px-3 py-2 text-base leading-relaxed text-tb-navy flex items-start gap-2">
                  <span className="flex-1 whitespace-pre-wrap">{t.text}</span>
                  <FactSourceInfoBadge fact={t}
                    testId={`compare-tip-${sideIndex}-${i}`} />
                </li>
              ))}
              {advisorTips.length > 3 && (
                <li className="text-xs font-hand text-tb-navy/60">
                  +{advisorTips.length - 3} more on the folder page
                </li>
              )}
            </ul>
          </section>
        )}
        {/* Narrative facts */}
        {narrative.length > 0 && (
          <section className="mt-5" data-testid={`atlas-compare-narrative-${sideIndex}`}>
            <div className="text-sm font-semibold text-tb-navy/70 mb-2">
              Narrative facts ({narrative.length})
            </div>
            <ul className="space-y-2">
              {narrative.slice(0, 3).map((n, i) => (
                <li key={i} className="rounded-lg border border-tb-navy/10 bg-white px-3 py-2 text-base leading-relaxed text-tb-navy flex items-start gap-2">
                  <span className="flex-1 whitespace-pre-wrap">{n.text}</span>
                  <FactSourceInfoBadge fact={n}
                    testId={`compare-narrative-${sideIndex}-${i}`} />
                </li>
              ))}
              {narrative.length > 3 && (
                <li className="text-xs font-hand text-tb-navy/60">
                  +{narrative.length - 3} more on the folder page
                </li>
              )}
            </ul>
          </section>
        )}
        {/* Empty-state hints */}
        {attrSchema.length === 0 && advisorTips.length === 0 && narrative.length === 0 && (
          <div className="mt-4 rounded-lg border border-dashed border-tb-navy/15 p-3 text-center">
            <p className="text-xs font-hand text-tb-navy/50">
              No attributes / tips / facts yet.{" "}
              <Link to={`/atlas/topics/${card.topic_slug}`}
                    className="underline underline-offset-2 hover:text-tb-navy">
                Add on the folder page →
              </Link>
            </p>
          </div>
        )}
        {/* Documents (PDFs) — clickable chips opening in a new tab. */}
        {pdfs.length > 0 && (
          <section className="mt-4" data-testid={`atlas-compare-documents-${sideIndex}`}>
            <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50 mb-1">
              Documents ({pdfs.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {pdfs.map((p, i) => (
                <a key={i} href={resolveUrl(p.url)} target="_blank" rel="noreferrer"
                  data-testid={`atlas-compare-pdf-chip-${sideIndex}-${i}`}
                  className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-tb-navy/5 hover:bg-tb-navy/10 text-tb-navy font-hand text-xs">
                  <span aria-hidden>📄</span>
                  <span className="max-w-[220px] truncate">{p.name || "document.pdf"}</span>
                </a>
              ))}
            </div>
          </section>
        )}
        {/* Sub-folders */}
        {subFolders.length > 0 && (
          <section className="mt-4"
                   data-testid={`atlas-compare-subfolders-${sideIndex}`}>
            <button type="button"
              onClick={() => setSubFoldersOpen((v) => !v)}
              data-testid={`atlas-compare-subfolders-toggle-${sideIndex}`}
              className="w-full flex items-center justify-between gap-2 text-[10px] font-hand uppercase tracking-widest text-tb-navy/60 hover:text-tb-navy border-t border-tb-navy/10 pt-3">
              <span>Sub-folders ({subFolders.length})</span>
              <span className="text-tb-navy/50">{subFoldersOpen ? "▼" : "▶"}</span>
            </button>
            {subFoldersOpen && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {subFolders.map((sf) => (
                  <Link key={sf.slug} to={`/atlas/topics/${sf.slug}`}
                    data-testid={`atlas-compare-subfolder-chip-${sideIndex}-${sf.slug}`}
                    className="inline-flex px-2.5 h-7 items-center rounded-full bg-tb-navy/5 hover:bg-tb-navy/10 text-tb-navy font-hand text-xs">
                    {sf.name}
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </article>
  );
}


function AmbiguityCard({ side, sideIndex }) {
  return (
    <div
      className="rounded-2xl border border-amber-300 bg-amber-50/60 p-4"
      data-testid={`atlas-compare-ambiguity-${sideIndex}`}
    >
      <div className="text-tb-navy text-sm font-semibold"
           style={{ fontFamily: '"Playfair Display", serif' }}>
        Did you mean…?
      </div>
      <p className="mt-1 text-[11px] font-hand text-tb-navy/60">
        Multiple TravelBio folders matched <b>{side.label}</b>. Pick one:
      </p>
      <ul className="mt-2 space-y-1">
        {(side.ambiguous_candidates || []).map((c) => (
          <li key={c.slug}>
            <Link to={`/atlas/topics/${c.slug}`}
              data-testid={`atlas-compare-ambiguity-pick-${sideIndex}-${c.slug}`}
              className="block px-3 py-1.5 rounded-lg bg-white border border-tb-navy/15 text-tb-navy font-hand text-xs hover:border-tb-navy/40">
              <b>{c.name}</b>
              {c.breadcrumb_slugs?.length ? (
                <span className="ml-2 text-tb-navy/50">
                  (under {c.breadcrumb_slugs.slice(-1)[0]})
                </span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}


function PlaceholderCard({ side, sideIndex }) {
  return (
    <div
      className="rounded-2xl border border-dashed border-tb-navy/25 bg-tb-navy/[0.02] p-6 text-center"
      data-testid={`atlas-compare-placeholder-${sideIndex}`}
    >
      <div className="text-tb-navy text-sm font-semibold"
           style={{ fontFamily: '"Playfair Display", serif' }}>
        Not in TravelBio yet
      </div>
      <p className="mt-1 text-[11px] font-hand text-tb-navy/60">
        <b>{side.label}</b> isn't a folder in your Atlas. Turn off <b>DB Only</b>
        {" "}to search Exa, or open the search bar to create it.
      </p>
    </div>
  );
}


function IntentPill({ label, testId }) {
  return (
    <span
      data-testid={testId}
      className="text-[10px] uppercase tracking-widest font-hand rounded-full border border-tb-navy/20 bg-tb-navy/[0.03] px-2 py-0.5 text-tb-navy"
    >
      {label}
    </span>
  );
}


function ColumnsSkeleton({ count }) {
  return (
    <div className="mt-6 flex gap-4" data-testid="atlas-search-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="w-[380px] flex-shrink-0 rounded-2xl border border-tb-navy/10 bg-tb-navy/[0.02] p-4 animate-pulse">
          <div className="h-4 w-32 bg-tb-navy/10 rounded" />
          <div className="mt-4 space-y-3">
            {Array.from({ length: 5 }).map((__, j) => (
              <div key={j} className="rounded-lg border border-tb-navy/10 bg-white p-3">
                <div className="w-full aspect-[3/2] bg-tb-navy/10 rounded-lg" />
                <div className="mt-2 h-3 w-40 bg-tb-navy/10 rounded" />
                <div className="mt-2 h-3 w-full bg-tb-navy/10 rounded" />
                <div className="mt-1 h-3 w-3/4 bg-tb-navy/10 rounded" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function ColumnPanel({ slug, column, enrichedById, enriching, onRemove, onViewMore, paging, onCardClick, savedByUrl, onOpenSavePicker }) {
  const results = column?.results || [];

  // Msg 547 — editorial coverage pill. Only visible after enrichment
  // (column.expanded === true means the server returned expanded_results
  // that include publication_name attribution). Aggregate top 3 pubs,
  // group unattributed as "uncredited", hide pill if literally nothing
  // is attributed.
  const editorial = useMemo(() => {
    if (!column?.expanded) return null;
    const buckets = new Map();
    let uncredited = 0;
    for (const r of results) {
      const pub = (r.publication_name || "").trim();
      if (pub) {
        buckets.set(pub, (buckets.get(pub) || 0) + 1);
      } else {
        uncredited += 1;
      }
    }
    if (buckets.size === 0) return null; // hide entirely — no attribution
    const ranked = Array.from(buckets.entries())
      .sort((a, b) => b[1] - a[1]);
    const top = ranked.slice(0, 3);
    const rest = ranked.slice(3).reduce((s, [, n]) => s + n, 0);
    return { top, restCount: rest, uncredited };
  }, [column?.expanded, results]);

  return (
    <div className="w-[380px] flex-shrink-0 rounded-2xl border border-tb-navy/15 bg-white"
         data-testid={`atlas-search-column-${slug}`}>
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl border-b border-tb-navy/10 bg-white/95 backdrop-blur p-3">
        <div className="flex-1 min-w-0">
          <div className="text-tb-navy font-semibold text-lg truncate" style={{ fontFamily: '"Playfair Display", serif' }}>
            {column?.name || slug.replace(/-/g, " ")}
          </div>
          <div className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50">
            {results.length} result{results.length === 1 ? "" : "s"}
            {column?.cached && <span className="ml-1 opacity-60">· cached</span>}
            {enriching && <span className="ml-1 text-tb-navy/70">· enriching…</span>}
          </div>
          {editorial && (
            <div className="mt-1 font-hand text-[10px] text-tb-navy/60 truncate"
                 data-testid={`atlas-search-column-editorial-${slug}`}
                 title={editorial.top.map(([p, n]) => `${n} ${p}`).join(" · ")}>
              📰 {editorial.top.map(([p, n]) => `${n} ${p}`).join(" · ")}
              {editorial.restCount > 0 && (
                <span className="opacity-60"> · {editorial.restCount} more</span>
              )}
              {editorial.uncredited > 0 && (
                <span className="opacity-60"> · {editorial.uncredited} uncredited</span>
              )}
            </div>
          )}
        </div>
        <button type="button" onClick={onRemove}
          data-testid={`atlas-search-column-remove-${slug}`}
          className="w-7 h-7 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60 hover:bg-tb-navy/5"
          title="Remove column">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-3 max-h-[calc(100vh-14rem)] overflow-y-auto">
        {results.length === 0 ? (
          <div className="rounded-xl border border-dashed border-tb-navy/15 bg-tb-navy/[0.02] p-4">
            <p className="font-hand text-tb-navy text-sm">No results for this column.</p>
            <p className="mt-1 font-hand text-tb-navy/50 text-xs">
              Try rewording your query, or remove this column and add a different destination.
            </p>
          </div>
        ) : (
          results.map((r, i) => (
            <ResultCard
              key={`${slug}-${i}-${r.url}`}
              slug={slug}
              r={r}
              enrichment={enrichedById[r.result_id]}
              enriching={enriching}
              onClick={() => onCardClick(r)}
              savedCount={(savedByUrl?.[r.url] || []).length}
              onSave={(e) => { e.stopPropagation(); onOpenSavePicker(r); }}
            />
          ))
        )}

        {results.length > 0 && column?.has_more && (
          <button type="button" onClick={onViewMore} disabled={paging}
            data-testid={`atlas-search-viewmore-${slug}`}
            className="w-full h-10 rounded-full border border-tb-navy/20 bg-white font-hand text-xs text-tb-navy hover:bg-tb-navy/5 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
            {paging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            View more
          </button>
        )}
      </div>
    </div>
  );
}


function AddColumnPanel({ open, onOpenChange, onAdd, busy }) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  useEffect(() => { if (!open) { setQuery(""); setSuggestions([]); } }, [open]);
  useEffect(() => {
    if (!open || query.trim().length < 2) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/atlas/topics?q=${encodeURIComponent(query)}&limit=20`);
        setSuggestions((data?.topics || []).slice(0, 12));
      } catch { setSuggestions([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  if (!open) {
    return (
      <button type="button" onClick={() => onOpenChange(true)} disabled={busy}
        data-testid="atlas-search-add-column-btn"
        className="w-[260px] flex-shrink-0 rounded-2xl border-2 border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-4 flex flex-col items-center justify-center text-tb-navy/60 hover:border-tb-navy/40 hover:bg-tb-navy/[0.04] transition disabled:opacity-40 min-h-[220px]">
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" />}
        <span className="mt-2 font-hand text-sm">Add destination</span>
      </button>
    );
  }
  return (
    <div className="w-[320px] flex-shrink-0 rounded-2xl border border-tb-navy/20 bg-white p-3" data-testid="atlas-search-add-column-panel">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/60 flex-1">Add destination</p>
        <button type="button" onClick={() => onOpenChange(false)}
                data-testid="atlas-search-add-column-close"
                className="w-6 h-6 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60 hover:bg-tb-navy/5">
          <X className="w-3 h-3" />
        </button>
      </div>
      <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder="e.g. Costa Rica, Iceland, Kyoto"
        data-testid="atlas-search-add-column-input"
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(query.trim()); } if (e.key === "Escape") onOpenChange(false); }}
        className="w-full h-9 px-2 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
      <div className="mt-2 max-h-64 overflow-y-auto space-y-1">
        {loading && <div className="font-hand text-[11px] text-tb-navy/40 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> searching…</div>}
        {!loading && suggestions.length === 0 && query.trim().length >= 2 && (
          <button type="button" onClick={() => onAdd(query.trim())} data-testid="atlas-search-add-column-freeform"
            className="w-full text-left rounded-lg border border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-2 font-hand text-xs text-tb-navy hover:bg-tb-navy/5">
            + Add "<span className="">{query.trim()}</span>" (freeform)
          </button>
        )}
        {suggestions.map((t) => (
          <button key={t.slug} type="button" onClick={() => onAdd(t.slug)}
            data-testid={`atlas-search-add-column-suggest-${t.slug}`}
            className="w-full text-left rounded-lg border border-tb-navy/10 bg-white p-2 font-hand text-xs text-tb-navy hover:bg-tb-navy/5">
            <div className="font-semibold text-sm">{t.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-tb-navy/50">{t.category?.replace(/_/g, " ")} · {t.fact_count || 0} facts</div>
          </button>
        ))}
      </div>
    </div>
  );
}


function ResultCard({ slug, r, enrichment, enriching, onClick, savedCount, onSave }) {
  const en = enrichment || {};
  const attr = en.travelbio_attribute_facts || [];
  const tips = en.travelbio_advisor_tips || [];
  const reviews = en.travelbio_traveler_reviews || [];
  const promos = en.travelbio_active_promos || [];
  const notices = en.travelbio_active_notices || [];
  const hasPromo = promos.length > 0;
  const reasoning = en.match_reasoning || "";

  return (
    <div
      className="relative rounded-xl border border-tb-navy/12 bg-white overflow-hidden hover:border-tb-navy/25 hover:shadow-sm cursor-pointer transition"
      data-testid={`atlas-search-result-${slug}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}
      role="button"
      tabIndex={0}
    >
      {hasPromo && (
        <div className="absolute top-1.5 right-1.5 z-10 bg-red-600 text-white text-[9px] uppercase tracking-widest font-hand px-2 py-0.5 rounded-full"
             data-testid={`atlas-search-result-promo-ribbon-${slug}`}
             title={`${promos.length} active promo${promos.length === 1 ? "" : "s"}`}>
          🎁 {promos.length} promo{promos.length === 1 ? "" : "s"}
        </div>
      )}
      {/* Msg 550 — bookmark toggle (fill = already saved for some client). */}
      <button type="button" onClick={onSave}
        data-testid={`atlas-search-result-save-${slug}`}
        title={savedCount > 0 ? `Saved to ${savedCount} client portfolio${savedCount === 1 ? "" : "s"}` : "Save to a client's portfolio"}
        className={"absolute top-1.5 left-1.5 z-10 w-7 h-7 rounded-full inline-flex items-center justify-center border transition " +
          (savedCount > 0
            ? "bg-tb-navy border-tb-navy text-tb-gold-bright"
            : "bg-white/90 backdrop-blur border-tb-navy/25 text-tb-navy/70 hover:bg-white")}>
        <Bookmark className={"w-3.5 h-3.5 " + (savedCount > 0 ? "fill-current" : "")} />
      </button>
      {/* Hero photo — Msg 544 · Item 2. */}
      {r.image_url && (
        <div className="w-full aspect-[3/2] bg-tb-navy/5 overflow-hidden">
          <img
            src={r.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            data-testid={`atlas-search-result-image-${slug}`}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}
      <div className="p-3">
        <p className="text-tb-navy font-semibold text-sm leading-tight line-clamp-2" style={{ fontFamily: '"Playfair Display", serif' }}>
          {r.title || "(untitled)"}
        </p>
        {/* Msg 545 · listicle expansion — subtle citation for cards
            extracted from roundup articles. */}
        {r.source_kind === "listicle_entity" && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-hand text-tb-navy/50"
               data-testid={`atlas-search-result-listicle-${slug}`}>
            📰 From: <span className="text-tb-navy/80">{r.publication_name || r.source_domain || "article"}</span>
          </div>
        )}
        {r.source_kind === "general_content" && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-hand text-tb-navy/50"
               data-testid={`atlas-search-result-general-${slug}`}>
            📰 General guide
          </div>
        )}
        {r.snippet && (
          <p className="mt-1 font-hand text-tb-navy/70 text-xs line-clamp-2">{r.snippet}</p>
        )}
        <div className="mt-1.5 flex items-center gap-2 font-hand text-[10px] text-tb-navy/50">
          {r.source_domain && <span data-testid={`atlas-search-result-domain-${slug}`}>🌐 {r.source_domain}</span>}
          {r.published_date && <span>· {new Date(r.published_date).toLocaleDateString()}</span>}
        </div>

        {/* Match reasoning — streamed in during Stage 2. */}
        {enriching && !reasoning ? (
          <div className="mt-2 animate-pulse">
            <div className="h-3 w-full bg-tb-navy/10 rounded" />
            <div className="mt-1 h-3 w-2/3 bg-tb-navy/10 rounded" />
          </div>
        ) : reasoning ? (
          <p data-testid={`atlas-search-result-reasoning-${slug}`}
             className="mt-2 font-hand text-[11px] text-tb-navy/70 leading-snug">
            {reasoning}
          </p>
        ) : null}

        {(attr.length > 0 || tips.length > 0 || reviews.length > 0 || notices.length > 0) && (
          <div className="mt-3 pt-3 border-t border-tb-navy/8 space-y-2"
               data-testid={`atlas-search-result-overlay-${slug}`}>
            <p className="text-[9px] uppercase tracking-widest font-hand text-tb-navy/40">TravelBio brain</p>
            {attr.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {attr.slice(0, 4).map((f) => (
                  <span key={f.id} title={f.text}
                    className="text-[10px] font-hand rounded-full border border-tb-navy/15 bg-white px-2 py-0.5 text-tb-navy/80">
                    🔧 {f.text.length > 34 ? f.text.slice(0, 32) + "…" : f.text}
                  </span>
                ))}
              </div>
            )}
            {tips.length > 0 && (
              <div className="inline-flex items-center gap-1 text-[10px] font-hand rounded-full bg-tb-gold-bright/20 border border-tb-gold-bright/70 text-tb-navy px-2 py-0.5"
                   data-testid={`atlas-search-result-tip-${slug}`}>
                💡 {tips.length} advisor tip{tips.length === 1 ? "" : "s"}
              </div>
            )}
            {reviews.length > 0 && (
              <div className="inline-flex items-center gap-1 text-[10px] font-hand rounded-full bg-sky-50 border border-sky-300 text-sky-800 px-2 py-0.5"
                   data-testid={`atlas-search-result-reviews-${slug}`}>
                ✈️ {reviews.length} traveler review{reviews.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function DetailDrawer({ query, slug, result, intent, preloadedEnrichment, savedByUrl, onOpenSavePicker, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/atlas/search/result-detail", {
          query, parsed_intent: intent, destination_slug: slug, result,
        });
        if (!cancelled) setDetail(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[atlas.search.detail] fail", err);
        if (!cancelled) setDetail({ match_percentage: null, match_justification: "", llm_summary: "" });
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [query, slug, result, intent]);

  // Prefer detail-endpoint overlays; fall back to whatever the Stage 2
  // enrichment already gave us so we render immediately.
  const attr = (detail?.travelbio_attribute_facts) || (preloadedEnrichment?.travelbio_attribute_facts) || [];
  const tips = (detail?.travelbio_advisor_tips) || (preloadedEnrichment?.travelbio_advisor_tips) || [];
  const reviews = (detail?.travelbio_traveler_reviews) || (preloadedEnrichment?.travelbio_traveler_reviews) || [];
  const promos = (detail?.travelbio_active_promos) || (preloadedEnrichment?.travelbio_active_promos) || [];
  const notices = (detail?.travelbio_active_notices) || (preloadedEnrichment?.travelbio_active_notices) || [];
  const pct = detail?.match_percentage;

  const savePortfolio = () => {
    // Msg 550 — open the client picker. Merge the live overlay slugs
    // so the snapshot carries the entity_slugs the enrich step resolved.
    onOpenSavePicker({
      ...result,
      entity_slugs: result.entity_slugs || detail?.travelbio_resolved_slugs || [],
    });
  };
  const savedCount = ((savedByUrl || {})[result.url] || []).length;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="atlas-search-detail-drawer">
      <div className="absolute inset-0 bg-tb-navy/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-[720px] h-full bg-white shadow-2xl overflow-y-auto">
        {result.image_url && (
          <div className="w-full aspect-[16/8] bg-tb-navy/5 overflow-hidden">
            <img src={result.image_url} alt=""
                 className="w-full h-full object-cover"
                 data-testid="atlas-search-detail-hero" />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-tb-navy font-semibold text-2xl leading-tight"
                style={{ fontFamily: '"Playfair Display", serif' }}
                data-testid="atlas-search-detail-title">
              {result.title || "(untitled)"}
            </h2>
            <button type="button" onClick={onClose}
                    data-testid="atlas-search-detail-close"
                    className="w-9 h-9 rounded-full inline-flex items-center justify-center border border-tb-navy/15 text-tb-navy/60 hover:bg-tb-navy/5">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-2 flex items-center gap-3">
            {loading ? (
              <div className="inline-flex items-center gap-1.5 font-hand text-xs text-tb-navy/50">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring match…
              </div>
            ) : pct != null ? (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 font-hand"
                   data-testid="atlas-search-detail-matchpct">
                <span className="text-emerald-800 font-semibold text-lg">{pct}%</span>
                <span className="text-[10px] uppercase tracking-widest text-emerald-800/70">match</span>
              </div>
            ) : null}
            {result.source_domain && (
              <span className="font-hand text-xs text-tb-navy/60">🌐 {result.source_domain}</span>
            )}
          </div>

          {detail?.match_justification && (
            <p className="mt-2 font-hand text-[11px] text-tb-navy/60">
              {detail.match_justification}
            </p>
          )}

          {result.source_kind === "listicle_entity" && result.article_url && (
            <div className="mt-3 rounded-lg bg-tb-navy/[0.03] border border-tb-navy/10 p-2.5"
                 data-testid="atlas-search-detail-article-citation">
              <p className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50 mb-1">
                📰 Source article
              </p>
              <a href={result.article_url} target="_blank" rel="noreferrer"
                 className="font-hand text-xs text-tb-navy underline underline-offset-2">
                {result.article_title || result.article_url}
              </a>
              {result.publication_name && (
                <div className="mt-0.5 font-hand text-[10px] text-tb-navy/50">
                  {result.publication_name}
                </div>
              )}
            </div>
          )}
          {Array.isArray(result.additional_citations) && result.additional_citations.length > 0 && (
            <div className="mt-3 rounded-lg bg-tb-navy/[0.03] border border-tb-navy/10 p-2.5"
                 data-testid="atlas-search-detail-additional-citations">
              <p className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50 mb-1">
                📰 Also mentioned in
              </p>
              <ul className="space-y-1">
                {result.additional_citations.map((cit, idx) => (
                  <li key={idx} className="font-hand text-xs">
                    <a href={cit.article_url} target="_blank" rel="noreferrer"
                       className="text-tb-navy underline underline-offset-2">
                      {cit.article_title || cit.article_url}
                    </a>
                    {cit.publication_name && (
                      <span className="ml-1 text-tb-navy/50 text-[10px]">
                        — {cit.publication_name}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail?.llm_summary ? (
            <p className="mt-4 font-hand text-sm text-tb-navy leading-relaxed"
               data-testid="atlas-search-detail-summary">
              {detail.llm_summary}
            </p>
          ) : loading ? (
            <div className="mt-4 space-y-2 animate-pulse">
              <div className="h-3 w-full bg-tb-navy/10 rounded" />
              <div className="h-3 w-5/6 bg-tb-navy/10 rounded" />
              <div className="h-3 w-2/3 bg-tb-navy/10 rounded" />
            </div>
          ) : result.snippet ? (
            <p className="mt-4 font-hand text-sm text-tb-navy/70 leading-relaxed">{result.snippet}</p>
          ) : null}

          {/* Msg 552 · Bug 4 — always-visible "+ Add" CTAs that
              deep-link into the Knowledge Builder with the entity
              name + tags + display_type pre-filled. */}
          <div className="mt-6" data-testid="atlas-search-detail-add-slots">
            <p className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50 mb-2">
              Contribute to the TravelBio brain
            </p>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const entityName = result.entity_name || result.title || "";
                const tags = (result.entity_slugs
                                || detail?.travelbio_resolved_slugs
                                || []).slice(0, 6).join(",");
                const qs = new URLSearchParams();
                if (entityName) qs.set("entity_name", entityName);
                if (tags) qs.set("tags", tags);
                const factHref = `/atlas/knowledge?${qs.toString()}&display_type=attribute`;
                const tipHref = `/atlas/knowledge?${qs.toString()}&display_type=advisor_tip`;
                return (
                  <>
                    <a href={factHref}
                       data-testid="atlas-search-detail-add-fact"
                       className="inline-flex items-center gap-1 rounded-full border border-tb-navy/20 bg-white px-3 py-1.5 font-hand text-xs text-tb-navy hover:bg-tb-navy/5">
                      🔧 + Add a fact about this
                    </a>
                    <a href={tipHref}
                       data-testid="atlas-search-detail-add-tip"
                       className="inline-flex items-center gap-1 rounded-full border border-tb-gold-bright/70 bg-tb-gold-bright/10 px-3 py-1.5 font-hand text-xs text-tb-navy hover:bg-tb-gold-bright/25">
                      💡 + Add an advisor tip
                    </a>
                  </>
                );
              })()}
            </div>
          </div>

          {attr.length > 0 && (
            <div className="mt-6" data-testid="atlas-search-detail-attributes">
              <p className="text-[10px] uppercase tracking-widest font-hand text-tb-navy/50 mb-2">Quick facts</p>
              <div className="flex flex-wrap gap-1.5">
                {attr.map((f) => (
                  <span key={f.id}
                        className="text-[11px] font-hand rounded-full border border-tb-navy/15 bg-white px-2.5 py-1 text-tb-navy">
                    🔧 {f.text}
                  </span>
                ))}
              </div>
            </div>
          )}

          {tips.length > 0 && (
            <div className="mt-6" data-testid="atlas-search-detail-tips">
              <button type="button" onClick={() => setTipsOpen((v) => !v)}
                className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-hand text-tb-navy/60 mb-2">
                💡 TravelBio Advisor Tips ({tips.length})
              </button>
              {tipsOpen && (
                <ul className="space-y-2">
                  {tips.map((t) => (
                    <li key={t.id} className="rounded-lg bg-tb-gold-bright/10 border border-tb-gold-bright/50 p-3 font-hand text-sm text-tb-navy">
                      "{t.text}"
                      <div className="mt-1 text-[10px] text-tb-navy/50 uppercase tracking-widest">
                        — {t.contributor_advisor_name || "advisor"} · ❤️ {t.helpful_count || 0}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {promos.length > 0 && (
            <div className="mt-6" data-testid="atlas-search-detail-promos">
              <p className="text-[10px] uppercase tracking-widest font-hand text-red-900/70 mb-2">🎁 Current promos</p>
              <ul className="space-y-2">
                {promos.map((p) => (
                  <li key={p.id} className="rounded-lg bg-red-50 border border-red-200 p-3">
                    <p className="font-hand text-sm text-tb-navy">{p.text}</p>
                    <div className="mt-1 font-hand text-[11px] text-red-900/80">
                      Book by {p.booking_window_end ? new Date(p.booking_window_end).toLocaleDateString() : (p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—")}
                      {(p.travel_window_start || p.travel_window_end) && (
                        <> · Travel {p.travel_window_start ? new Date(p.travel_window_start).toLocaleDateString() : "—"} – {p.travel_window_end ? new Date(p.travel_window_end).toLocaleDateString() : "—"}</>
                      )}
                    </div>
                    {p.offer_url && (
                      <a href={p.offer_url} target="_blank" rel="noreferrer"
                         className="mt-1 inline-block font-hand text-[10px] text-red-700 underline">View offer →</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {notices.length > 0 && (
            <div className="mt-6" data-testid="atlas-search-detail-notices">
              <p className="text-[10px] uppercase tracking-widest font-hand text-amber-900/70 mb-2">⚠️ Operational notices</p>
              <ul className="space-y-2">
                {notices.map((n) => (
                  <li key={n.id} className="rounded-lg bg-amber-50 border border-amber-300 p-3 font-hand text-sm text-tb-navy">
                    {n.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reviews.length > 0 && (
            <div className="mt-6" data-testid="atlas-search-detail-reviews">
              <button type="button" onClick={() => setReviewsOpen((v) => !v)}
                data-testid="atlas-search-detail-reviews-toggle"
                className="inline-flex items-center gap-1 text-[11px] font-hand rounded-full bg-sky-50 border border-sky-300 text-sky-800 px-3 py-1 hover:bg-sky-100">
                ✈️ {reviews.length} traveler review{reviews.length === 1 ? "" : "s"} — {reviewsOpen ? "hide" : "show"}
              </button>
              {reviewsOpen && (
                <ul className="mt-2 space-y-1.5">
                  {reviews.map((rv) => (
                    <li key={rv.id} className="rounded-md bg-sky-50/50 border border-sky-100 px-3 py-2 font-hand text-xs text-tb-navy/80">
                      {rv.text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-tb-navy/10 flex flex-wrap items-center gap-2">
            <button type="button" onClick={savePortfolio}
              data-testid="atlas-search-detail-save"
              className={"inline-flex items-center gap-1.5 h-10 px-4 rounded-full font-hand text-sm hover:opacity-90 " +
                (savedCount > 0
                  ? "bg-tb-navy text-tb-gold-bright"
                  : "bg-tb-navy text-tb-gold-bright")}>
              <Bookmark className={"w-4 h-4 " + (savedCount > 0 ? "fill-current" : "")} />
              {savedCount > 0
                ? `Saved to ${savedCount} client${savedCount === 1 ? "" : "s"} · Save to another`
                : "Save to client portfolio"}
            </button>
            <a href={result.url} target="_blank" rel="noreferrer"
               data-testid="atlas-search-detail-view-original"
               className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
              <ExternalLink className="w-4 h-4" />
              View original source
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
